import { Injectable } from '@angular/core';

import moment from 'moment-timezone';

import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';

import { Am5CategoryValueXYBaseChartSettings } from '../../Charts/models/Am5CategoryValueXYChart/am5-category-value-x-y-base-chart-settings.model';
import { Am5ChartResponsiveRulesService } from './am5-chart-responsive-rules.service';
import { Am5ChartSettings } from '../../Charts/models/am5-chart-settings.model';
import { Am5DateValueXYChartSettings } from '../../Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart-settings.model';
import { Am5SentimentTrendXYChartSettings } from '../../../Common/Charts/models/Am5SentimentTrendXYChart/am5-sentiment-trend-x-y-chart-settings.model';
import { annotatableBlockTypes } from '../models/new-data-processing-workflow-block-types.model';
import { chartColors } from '../../../Common/Charts/enums/chart-colors.enum';
import { ChartDisplayElementModel } from '../models/chart-display-element.model';
import { ChartSizeHelper } from '../../Charts/helpers/chart-size.helper';
import { ChartSpecificTypesEnum } from '../enums/chart-specific-types.enum';
import { ChartTimeIntervalsEnum, defaultChartTypesToTimeIntervalsMap } from '../enums/chart-time-intervals.enum';
import { DateGranularityModel } from '../../Charts/models/date-granularity.model';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { ReportBlockTypeEnum } from '../enums/report-block-type.enum';
import { TimeHelper, timeIntervalsToDategranularitiesMap } from '../../../Common/Forms/helpers/time.helper';


@Injectable()
export class Am5ChartSettingsService {

    timeHelper = new TimeHelper();

    readonly maxDataPoints = 36;
    readonly timeIntervalsToMaxHoursForFixedGroupingMap: Map<ChartTimeIntervalsEnum, number> = new Map([
        [ChartTimeIntervalsEnum.WEEK, 24 * 7 * this.maxDataPoints + 2],
        [ChartTimeIntervalsEnum.MONTH, 24 * 7 * 31 * this.maxDataPoints + 2],
        [ChartTimeIntervalsEnum.QUARTER, 24 * 7 * 31 * 3 * this.maxDataPoints + 2],
        [ChartTimeIntervalsEnum.YEAR, 24 * 7 * 31 * 12 * this.maxDataPoints + 2]
    ]);

    constructor(
        public am5ChartResponsiveRulesService: Am5ChartResponsiveRulesService
    ) {}

    public getChartSettings(type: ReportBlockTypeEnum, displayElement: ChartDisplayElementModel, rows?: number, cols?: number): Am5ChartSettings {
        const dateRangeInHours = DateQueryModel.getTotalHoursInQuery(displayElement.series[0]?.dateRange);
        const timeInterval = displayElement.timeInterval ?? defaultChartTypesToTimeIntervalsMap.get(type);
        const baseSettings = {
            titleLabelText: displayElement.title,
            displaySettings: displayElement.styles.chartDisplaySettings,
            chartSpecificType: displayElement.chartSpecificType,
            groupData: timeInterval === ChartTimeIntervalsEnum.DYNAMIC,
            groupInterval: this.getGroupInterval(dateRangeInHours, displayElement),
            labelDisplayOptions: displayElement.labelDisplayOptions,
            rows: rows,
            cols: cols,
            width: ChartSizeHelper.getChartGridWidth(cols),
            height: ChartSizeHelper.getChartGridHeight(rows),
            dateGranularity: this.timeHelper.getDateGranularity(dateRangeInHours, displayElement.timeInterval),
            colorMap: displayElement.styles.colorMap,
            responsiveRules: this.am5ChartResponsiveRulesService.getResponsiveRules(type, displayElement.chartSpecificType)
        } as Am5ChartSettings;
        let settings = { ...baseSettings } as Am5ChartSettings;
        if (annotatableBlockTypes.includes(type)) {
            settings = {
                ...settings,
                chartAnnotations: displayElement.chartAnnotations
            };
        }
        switch (type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                settings = {
                    ...settings,
                    events: displayElement.prevents,
                    isSingleSeries: displayElement.series?.length === 1,
                    timeInterval: displayElement.timeInterval
                } as Am5DateValueXYChartSettings;
                break;
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                settings = {
                    ...settings,
                    stackedChartType: displayElement.stackedChartTypes,
                    sentiments: displayElement.series[0].sentiments,
                    colorMap: {
                        [SearchCriteriaQuerySentimentsEnum.positive]: chartColors.agprPositive,
                        [SearchCriteriaQuerySentimentsEnum.neutral]: chartColors.agprNeutral,
                        [SearchCriteriaQuerySentimentsEnum.negative]: chartColors.agprNegative,
                        [SearchCriteriaQuerySentimentsEnum.notAvailable]: chartColors.agprNotAvailable,
                        ...displayElement.styles?.colorMap
                    },
                    responsiveRules: this.am5ChartResponsiveRulesService.getResponsiveRules(type, displayElement.chartSpecificType),
                    timeInterval: displayElement.timeInterval
                } as Am5SentimentTrendXYChartSettings;
                break;
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                return {
                    titleLabelText: displayElement.title,
                    displaySettings: displayElement.styles.chartDisplaySettings,
                    chartSpecificType: displayElement.chartSpecificType,
                    enableDataDisplaySettings: displayElement.enableDataDisplaySettings,
                    labelDisplayOptions: displayElement.labelDisplayOptions,
                    rows: rows,
                    cols: cols,
                    width: ChartSizeHelper.getChartGridWidth(cols),
                    height: ChartSizeHelper.getChartGridHeight(rows),
                    events: displayElement.prevents,
                    colorMap: displayElement.styles.colorMap,
                    chartAnnotations: displayElement.chartAnnotations,
                    tooltipPointerOrientation: 'left',
                    responsiveRules: this.am5ChartResponsiveRulesService.getResponsiveRules(type, displayElement.chartSpecificType)
                } as Am5CategoryValueXYBaseChartSettings;
        }

        return settings;
    }

    private getGroupInterval(totalHours: number, displayElement: ChartDisplayElementModel): DateGranularityModel | undefined {
        if (!displayElement.timeInterval || displayElement.timeInterval === ChartTimeIntervalsEnum.DYNAMIC || totalHours === Infinity) {
            return undefined;
        }
        const dateRange = displayElement.series[0]?.dateRange;
        if (dateRange.type === DateQueryTypeEnum.ABSOLUTE) {
            const absoluteDateRange = dateRange?.absoluteRange;
            if (!absoluteDateRange) {
                return undefined;
            }
            const timeIntervalsToMomentUnitsMap: Map<ChartTimeIntervalsEnum, moment.DurationInputArg2> = new Map([
                [ChartTimeIntervalsEnum.WEEK, 'weeks'],
                [ChartTimeIntervalsEnum.MONTH, 'months'],
                [ChartTimeIntervalsEnum.QUARTER, 'quarters'],
                [ChartTimeIntervalsEnum.YEAR, 'years']
            ]);
            const startMoment = moment(absoluteDateRange.start);
            const start = startMoment.valueOf();
            const fixedGroupingMinMoment = moment(absoluteDateRange.end)
                .subtract(this.maxDataPoints, timeIntervalsToMomentUnitsMap.get(displayElement.timeInterval))
                .subtract(2, 'hours');
            const fixedGroupingMinStart = fixedGroupingMinMoment.valueOf();
            if (start < fixedGroupingMinStart) {
                return undefined;
            }
        }

        return timeIntervalsToDategranularitiesMap.get(displayElement.timeInterval);
    }

    private isBarChart(chartSpecificType: ChartSpecificTypesEnum): boolean {
        return chartSpecificType === ChartSpecificTypesEnum.BAR ||
            chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR;
    }
}
